import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trophy, Users, CalendarBlank, Tree, ArrowLeft, Trash, PencilSimple, Broadcast, Medal, UsersThree, Clock } from "@phosphor-icons/react";
import ScoreInput, { ScoreDisplay } from "@/components/ScoreInput";
import PaymentUploader from "@/components/PaymentUploader";
import QuickReportButton from "@/components/QuickReportButton";
import EventMediaGallery from "@/components/EventMediaGallery";
import PendingApprovalsDialog from "@/components/PendingApprovalsDialog";
import WhatsAppShareButton from "@/components/WhatsAppShareButton";
import MultiClubSelect from "@/components/MultiClubSelect";
import { useStripeReturn } from "@/lib/useStripeReturn";
import { CATEGORIES, categoryShort } from "@/lib/locations";

const statusColors = {
  abierto: "bg-green-500/20 text-green-400 border-green-500/30",
  en_curso: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  finalizado: "bg-gray-500/20 text-gray-400 border-gray-500/30",
};

function BracketView({ tournament, onResultSubmit, canManage, onStartScoreboard }) {
  const brackets = tournament.brackets || [];
  const [resultDialog, setResultDialog] = useState(null);
  const [resultForm, setResultForm] = useState({ winner_id: "", score: "" });

  const handleSubmitResult = (e) => {
    e.preventDefault();
    if (!resultForm.winner_id) { return; }
    if (resultDialog) {
      onResultSubmit(resultDialog.roundNum, resultDialog.matchIndex, resultForm.winner_id, resultForm.score);
      setResultDialog(null);
    }
  };

  const handleScoreChange = ({ score, winnerId }) => {
    setResultForm({ score, winner_id: winnerId || "" });
  };

  if (brackets.length === 0) {
    return (
      <div className="card-tactical p-12 text-center">
        <Tree size={48} weight="duotone" className="text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400">No se han generado brackets aun</p>
        <p className="text-sm text-gray-600 mt-1">Inscribe jugadores y genera los brackets</p>
      </div>
    );
  }

  return (
    <div data-testid="bracket-view">
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-6 min-w-max">
          {brackets.map((round, ri) => (
            <div key={ri} className="flex flex-col" style={{ minWidth: 220 }}>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-4 text-center">{round.name}</p>
              <div className="flex flex-col justify-around gap-3 flex-1">
                {round.matches.map((match, mi) => {
                  const p1Won = match.winner_id === match.player1_id;
                  const p2Won = match.winner_id === match.player2_id;
                  const canSetResult = canManage && match.player1_id && match.player2_id && match.player1_id !== "BYE" && match.player2_id !== "BYE" && !match.winner_id;
                  return (
                    <div key={mi} className="card-tactical overflow-hidden" data-testid={`bracket-match-${ri}-${mi}`}>
                      <div className={`flex items-center justify-between px-3 py-2 border-b border-white/5 ${p1Won ? "bg-[#CCFF00]/10" : ""}`}>
                        <span className={`text-sm font-semibold ${p1Won ? "text-[#CCFF00]" : match.player1_id ? "text-white" : "text-gray-600"}`}>
                          {match.player1_name || "TBD"}
                        </span>
                        {p1Won && <Trophy size={14} className="text-[#CCFF00]" />}
                      </div>
                      <div className={`flex items-center justify-between px-3 py-2 ${p2Won ? "bg-[#CCFF00]/10" : ""}`}>
                        <span className={`text-sm font-semibold ${p2Won ? "text-[#CCFF00]" : match.player2_id ? "text-white" : "text-gray-600"}`}>
                          {match.player2_name || "TBD"}
                        </span>
                        {p2Won && <Trophy size={14} className="text-[#CCFF00]" />}
                      </div>
                      {match.score && match.score !== "BYE" && (
                        <div className="px-3 py-1 border-t border-white/5 bg-black/30">
                          <ScoreDisplay score={match.score} />
                        </div>
                      )}
                      {canSetResult && (
                        <button onClick={() => { setResultDialog({ roundNum: ri + 1, matchIndex: mi, match }); setResultForm({ winner_id: "", score: "" }); }}
                          className="w-full px-3 py-1.5 text-xs text-center bg-[#CCFF00]/10 text-[#CCFF00] hover:bg-[#CCFF00]/20 transition-colors uppercase font-bold tracking-wider"
                          data-testid={`set-result-${ri}-${mi}`}>
                          Registrar Resultado
                        </button>
                      )}
                      {canManage && match.player1_id && match.player2_id && match.player1_id !== "BYE" && match.player2_id !== "BYE" && (
                        match.scoreboard_match_id ? (
                          <Link to={`/dashboard/marcador/${match.scoreboard_match_id}`} className="block w-full px-3 py-1.5 text-xs text-center bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 transition-colors uppercase font-bold tracking-wider border-t border-white/5" data-testid={`view-scoreboard-${ri}-${mi}`}>
                            Ver marcador live
                          </Link>
                        ) : (
                          <button onClick={() => onStartScoreboard && onStartScoreboard(ri + 1, mi)}
                            className="w-full px-3 py-1.5 text-xs text-center bg-white/5 text-white hover:bg-white/10 transition-colors uppercase font-bold tracking-wider border-t border-white/5"
                            data-testid={`start-scoreboard-${ri}-${mi}`}>
                            Iniciar marcador
                          </button>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!resultDialog} onOpenChange={() => setResultDialog(null)}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-lg" data-testid="bracket-result-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase">Marcador - 3 Sets a 6 Juegos</DialogTitle></DialogHeader>
          {resultDialog && (
            <form onSubmit={handleSubmitResult} className="space-y-4">
              <ScoreInput
                player1Name={resultDialog.match.player1_name}
                player2Name={resultDialog.match.player2_name}
                player1Id={resultDialog.match.player1_id}
                player2Id={resultDialog.match.player2_id}
                onScoreChange={handleScoreChange}
              />
              <DialogFooter>
                <Button type="submit" disabled={!resultForm.winner_id} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide w-full disabled:opacity-40" data-testid="bracket-result-submit">Registrar</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Tournaments() {
  const { user, permissions } = useAuth();
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", club_id: "", club_ids: [], start_date: "", end_date: "", start_time: "", end_time: "", format: "eliminacion", max_players: 16, description: "", entry_fee: 0, category: "", league_id: "", require_approval: false });
  const [leagues, setLeagues] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [filterCategory, setFilterCategory] = useState("all");
  const [pendingFor, setPendingFor] = useState(null);

  const canCreate = permissions?.canCreateTournaments;
  // Edit/Delete: admin, directivo of club, or creator (jugador/profesor).
  const canManageTournament = (t) => {
    if (!user || !t) return false;
    if (user.role === "admin") return true;
    if (user.role === "directivo") {
      const tClubIds = new Set([...(t.club_ids || []), ...(t.club_id ? [t.club_id] : [])]);
      const uClubIds = new Set([...(user.club_ids || []), ...(user.club_id ? [user.club_id] : [])]);
      for (const cid of uClubIds) if (tClubIds.has(cid)) return true;
    }
    if (t.created_by && t.created_by === user.id) return true;
    return false;
  };

  useEffect(() => {
    Promise.all([api.get("/tournaments"), api.get("/clubs"), api.get("/leagues")])
      .then(([t, c, lg]) => { setTournaments(t.data); setClubs(c.data); setLeagues(lg.data || []); })
      .catch(() => toast.error("Error al cargar"))
      .finally(() => setLoading(false));
  }, []);

  const loadTournaments = () => api.get("/tournaments").then(({ data }) => { setTournaments(data); if (selectedTournament) { const updated = data.find(t => t.id === selectedTournament.id); if (updated) setSelectedTournament(updated); } });
  useStripeReturn(() => loadTournaments());

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const clubIds = (form.club_ids || []).filter(Boolean);
      if (clubIds.length === 0) {
        toast.error("Elige al menos un club");
        return;
      }
      await api.post("/tournaments", {
        ...form,
        club_id: clubIds[0],
        club_ids: clubIds,
        category: form.category || null,
        league_id: form.league_id || null,
        entry_fee: parseFloat(form.entry_fee) || 0,
        max_players: parseInt(form.max_players) || 16,
      });
      toast.success("Torneo creado");
      setDialogOpen(false);
      loadTournaments();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleEnroll = async (id) => {
    try {
      const { data } = await api.post(`/tournaments/${id}/enroll`);
      if (data?.pending) {
        toast.info("Solicitud enviada", { description: "Un administrador debe autorizar tu inscripción" });
      } else {
        toast.success("¡Inscrito!");
      }
      loadTournaments();
    }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleUnenroll = async (id) => {
    try { await api.post(`/tournaments/${id}/unenroll`); toast.success("Desinscrito"); loadTournaments(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleGenerateBrackets = async (id) => {
    try { const { data } = await api.post(`/tournaments/${id}/generate-brackets`); toast.success("Brackets generados!"); loadTournaments(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleBracketResult = async (roundNum, matchIndex, winnerId, score) => {
    try {
      await api.post(`/tournaments/${selectedTournament.id}/bracket-result`, { round_num: roundNum, match_index: matchIndex, winner_id: winnerId, score });
      toast.success("Resultado registrado!");
      loadTournaments();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleDeleteTournament = async (id) => {
    if (!window.confirm("Eliminar este torneo permanentemente?")) return;
    try {
      await api.delete(`/admin/tournaments/${id}`);
      toast.success("Torneo eliminado");
      setSelectedTournament(null);
      loadTournaments();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const openEditTournament = () => {
    const t = selectedTournament;
    const cids = Array.isArray(t.club_ids) && t.club_ids.length ? t.club_ids : (t.club_id ? [t.club_id] : []);
    setEditForm({ name: t.name, club_ids: cids, club_id: t.club_id || (cids[0] || ""), start_date: t.start_date, end_date: t.end_date, start_time: t.start_time || "", end_time: t.end_time || "", description: t.description || "", max_players: t.max_players, entry_fee: t.entry_fee || 0, status: t.status });
    setEditDialogOpen(true);
  };

  const handleEditTournament = async () => {
    try {
      const cids = (editForm.club_ids || []).filter(Boolean);
      if (cids.length === 0) {
        toast.error("Elige al menos un club");
        return;
      }
      await api.put(`/tournaments/${selectedTournament.id}`, {
        ...editForm,
        club_id: cids[0],
        club_ids: cids,
      });
      toast.success("Torneo actualizado");
      setEditDialogOpen(false);
      loadTournaments();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const update = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;

  // Detail view for a tournament
  if (selectedTournament) {
    const t = selectedTournament;
    const detailClubIds = Array.isArray(t.club_ids) && t.club_ids.length ? t.club_ids : (t.club_id ? [t.club_id] : []);
    const detailClubs = detailClubIds.map((cid) => clubs.find((c) => c.id === cid)).filter(Boolean);
    const detailClubLabel = detailClubs.length === 0 ? "" : detailClubs.length === 1 ? detailClubs[0].name : detailClubs.map((c) => c.name).join(" · ");
    const club = detailClubs[0];
    const isEnrolled = t.players?.includes(user?.id);
    return (
      <div className="space-y-6 animate-fade-in" data-testid="tournament-detail">
        <Button variant="ghost" onClick={() => setSelectedTournament(null)} className="text-gray-400 hover:text-[#CCFF00] -ml-2" data-testid="back-to-list">
          <ArrowLeft size={18} className="mr-2" /> Volver a Torneos
        </Button>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1" data-testid="tournament-detail-clubs">{detailClubLabel}</p>
            <h1 className="font-['Barlow_Condensed'] text-4xl font-black uppercase tracking-tighter">{t.name}</h1>
            <div className="flex gap-4 mt-2 text-xs text-gray-500 flex-wrap items-center">
              <span>📅 {t.start_date} - {t.end_date}</span>
              {(t.start_time || t.end_time) && <span>⏰ {t.start_time || "--"} - {t.end_time || "--"}</span>}
              <span>{t.players?.length || 0}/{t.max_players} jugadores</span>
              <Badge variant="outline" className={`badge-status ${statusColors[t.status]}`}>{t.status}</Badge>
              {t.league_id && (() => {
                const linkedLeague = leagues.find((lg) => lg.id === t.league_id);
                return (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/dashboard/ligas?league=${t.league_id}`)}
                    className="border-[#7C5CFF]/40 text-[#7C5CFF] hover:bg-[#7C5CFF]/10 text-xs uppercase h-7"
                    data-testid="tournament-detail-league-link"
                  >
                    <Medal size={14} className="mr-1" /> Ver liga vinculada{linkedLeague?.name ? `: ${linkedLeague.name}` : ""}
                  </Button>
                );
              })()}
            </div>
          </div>
          <div className="flex gap-2">
            <QuickReportButton section="tournaments" params={{ tournament_id: t.id }} variant="compact" />
            {t.status === "abierto" && canCreate && t.players?.length >= 2 && (
              <Button onClick={() => handleGenerateBrackets(t.id)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs" data-testid="generate-brackets-btn">
                <Tree size={16} className="mr-2" /> Generar Brackets
              </Button>
            )}
            {t.status === "abierto" && (isEnrolled ? (
              <Button variant="outline" onClick={() => handleUnenroll(t.id)} className="border-white/20 text-gray-300 text-xs uppercase font-bold" data-testid="detail-unenroll">Desinscribirse</Button>
            ) : (t.pending_players || []).includes(user?.id) ? (
              <Button variant="outline" onClick={() => handleUnenroll(t.id)} className="border-[#FFD24A]/40 text-[#FFD24A] text-xs uppercase font-bold" data-testid="detail-pending" title="Solicitud enviada · pendiente de aprobación">
                <Clock size={14} className="mr-1" /> Pendiente · Cancelar
              </Button>
            ) : (
              <Button onClick={() => handleEnroll(t.id)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs" data-testid="detail-enroll">
                {t.require_approval ? "Solicitar inscripción" : "Inscribirse"}
              </Button>
            ))}
            <Button onClick={async () => {
              try {
                const enrolledPlayers = (t.players || []).slice(0, 4);
                const { data } = await api.post("/matches", {
                  pair1_p1: enrolledPlayers[0] ? { id: enrolledPlayers[0] } : { name: "Jugador 1" },
                  pair1_p2: enrolledPlayers[1] ? { id: enrolledPlayers[1] } : { name: "Jugador 2" },
                  pair2_p1: enrolledPlayers[2] ? { id: enrolledPlayers[2] } : { name: "Jugador 3" },
                  pair2_p2: enrolledPlayers[3] ? { id: enrolledPlayers[3] } : { name: "Jugador 4" },
                  tournament_id: t.id,
                  club_id: t.club_id || null,
                  category: t.category || null,
                  date: t.start_date,
                  time: t.start_time || "",
                  format: "standard",
                });
                toast.success("Marcador iniciado");
                navigate(`/dashboard/marcador/${data.id}`);
              } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
            }} className="bg-[#CCFF00]/10 text-[#CCFF00] hover:bg-[#CCFF00]/20 border border-[#CCFF00]/30 text-xs uppercase font-bold" data-testid="tournament-scoreboard-btn">
              <Broadcast size={14} className="mr-1" /> Marcador
            </Button>
            <Button onClick={() => navigate(`/dashboard/torneos/${t.id}/grupos`)} className="bg-[#1E40AF]/20 text-[#93C5FD] hover:bg-[#1E40AF]/30 border border-[#1E40AF]/40 text-xs uppercase font-bold" data-testid="tournament-groups-btn">
              <UsersThree size={14} className="mr-1" /> Grupos
            </Button>
            {canManageTournament(t) && (t.pending_players?.length || 0) > 0 && (
              <Button onClick={() => setPendingFor(t)} className="bg-[#FFD24A]/15 text-[#FFD24A] hover:bg-[#FFD24A]/25 border border-[#FFD24A]/40 text-xs uppercase font-bold" data-testid="tournament-pending-btn">
                <Clock size={14} className="mr-1" /> Solicitudes ({t.pending_players.length})
              </Button>
            )}
            <EventMediaGallery entityType="tournaments" entityId={t.id} testIdPrefix="tournament-media" />
            <WhatsAppShareButton
              message={`Torneo: ${t.name}\nFechas: ${t.start_date}${t.end_date ? ` - ${t.end_date}` : ""}${t.start_time ? ` · ${t.start_time}` : ""}\n${t.category ? `Categoría: ${t.category}\n` : ""}Cuota: ${t.entry_fee > 0 ? `$${t.entry_fee}` : "Gratis"}\nInscritos: ${(t.players || []).length}\n\n¡Inscríbete en Disfruta y Diviértete!`}
              testId={`whatsapp-tournament-${t.id}`}
            />
            {canManageTournament(t) && (
              <>
                <Button variant="outline" onClick={openEditTournament} className="border-white/20 text-gray-300 hover:text-[#CCFF00] hover:border-[#CCFF00]/30 text-xs uppercase font-bold" data-testid="edit-tournament-btn">
                  <PencilSimple size={14} className="mr-1" /> Editar
                </Button>
                <Button variant="outline" onClick={() => handleDeleteTournament(t.id)} className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs uppercase font-bold" data-testid="delete-tournament-btn">
                  <Trash size={14} className="mr-1" /> Eliminar
                </Button>
              </>
            )}
          </div>
        </div>

        <Tabs defaultValue={t.brackets?.length > 0 ? "brackets" : "info"} className="mt-6">
          <TabsList className="bg-[#141414] border border-white/10">
            <TabsTrigger value="info" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black uppercase text-xs font-bold tracking-wider">Info</TabsTrigger>
            <TabsTrigger value="brackets" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black uppercase text-xs font-bold tracking-wider">Brackets</TabsTrigger>
          </TabsList>
          <TabsContent value="info" className="mt-4">
            <div className="card-tactical p-6">
              {t.description && <p className="text-gray-400 mb-4">{t.description}</p>}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Formato:</span> <span className="text-white uppercase">{t.format}</span></div>
                <div><span className="text-gray-500">Cuota:</span> <span className="text-white">{t.entry_fee > 0 ? `$${t.entry_fee}` : "Gratis"}</span></div>
                <div><span className="text-gray-500">Fechas:</span> <span className="text-white">{t.start_date} → {t.end_date}</span></div>
                <div><span className="text-gray-500">Horario:</span> <span className="text-white">{(t.start_time || t.end_time) ? `${t.start_time || "--"} - ${t.end_time || "--"}` : "Por definir"}</span></div>
              </div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500 mt-6 mb-3">Jugadores Inscritos ({t.players?.length || 0})</p>
              {t.players?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {t.players.map((pid, i) => <PlayerBadge key={pid} playerId={pid} index={i} />)}
                </div>
              ) : <p className="text-gray-600 text-sm">No hay jugadores inscritos</p>}

              {t.entry_fee > 0 && t.players?.length > 0 && (
                <>
                  <p className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500 mt-6 mb-3">Pagos de Inscripcion</p>
                  <div className="space-y-2">
                    {t.players.map((pid) => {
                      const pay = (t.payments || {})[pid] || { status: "pendiente" };
                      const canUpload = pid === user?.id;
                      const canApprove = ["admin", "directivo"].includes(user?.role);
                      if (pay.status === "pendiente" && !canUpload && !canApprove) return null;
                      return (
                        <div key={pid} className="flex items-center gap-3 flex-wrap bg-[#0A0A0A] border border-white/10 p-3">
                          <PlayerBadge playerId={pid} />
                          <PaymentUploader
                            entryFee={t.entry_fee}
                            uploadUrl={`/tournaments/${t.id}/upload-proof`}
                            proofUrl={`/tournaments/${t.id}/proof/${pid}`}
                            approveUrl={`/admin/tournaments/${t.id}/approve-payment/${pid}`}
                            stripeCheckoutUrl={canUpload ? `/tournaments/${t.id}/stripe-checkout` : undefined}
                            status={pay.status || "pendiente"}
                            canUpload={canUpload}
                            canApprove={canApprove}
                            onChange={loadTournaments}
                            testId={`tournament-pay-${t.id}-${pid}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </TabsContent>
          <TabsContent value="brackets" className="mt-4">
            {t.format === "round_robin" && t.standings?.length > 0 && (
              <div className="card-tactical p-5 mb-4" data-testid="rr-standings">
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Round Robin</p>
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight mb-3">Tabla de Posiciones</h3>
                <table className="w-full text-sm">
                  <thead><tr className="text-[10px] uppercase tracking-widest text-gray-500 border-b border-white/5">
                    <th className="text-left py-2">#</th><th className="text-left">Jugador</th><th className="text-right">PJ</th><th className="text-right">G</th><th className="text-right">P</th><th className="text-right">Pts</th>
                  </tr></thead>
                  <tbody>
                    {t.standings.map((s, i) => (
                      <tr key={s.player_id} className={`border-b border-white/5 ${i === 0 ? "bg-[#CCFF00]/5" : ""}`}>
                        <td className="font-['Barlow_Condensed'] text-lg font-black text-gray-600 py-2">{String(i+1).padStart(2,"0")}</td>
                        <td className={`font-semibold ${i === 0 ? "text-[#CCFF00]" : "text-white"}`}>{s.player_name}</td>
                        <td className="text-right font-['JetBrains_Mono'] text-gray-400">{s.played}</td>
                        <td className="text-right font-['JetBrains_Mono'] text-[#CCFF00]">{s.won}</td>
                        <td className="text-right font-['JetBrains_Mono'] text-red-400">{s.lost}</td>
                        <td className="text-right font-['JetBrains_Mono'] text-[#CCFF00] font-bold">{s.points}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {t.format === "grupos" && t.groups?.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-testid="groups-standings">
                {t.groups.map((g) => (
                  <div key={g.name} className="card-tactical p-5">
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">{g.name}</p>
                    <table className="w-full text-sm mt-2">
                      <thead><tr className="text-[10px] uppercase tracking-widest text-gray-500 border-b border-white/5">
                        <th className="text-left py-2">Jugador</th><th className="text-right">PJ</th><th className="text-right">G-P</th><th className="text-right">Pts</th>
                      </tr></thead>
                      <tbody>
                        {g.standings.map((s, i) => (
                          <tr key={s.player_id} className={`border-b border-white/5 ${i < 2 ? "bg-[#CCFF00]/5" : ""}`}>
                            <td className={`font-semibold py-2 ${i < 2 ? "text-[#CCFF00]" : "text-white"}`}>{s.player_name}</td>
                            <td className="text-right font-['JetBrains_Mono'] text-gray-400">{s.played}</td>
                            <td className="text-right font-['JetBrains_Mono']"><span className="text-[#CCFF00]">{s.won}</span>-<span className="text-red-400">{s.lost}</span></td>
                            <td className="text-right font-['JetBrains_Mono'] text-[#CCFF00] font-bold">{s.points}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-[10px] text-gray-600 mt-2 uppercase tracking-widest">Los 2 primeros avanzan</p>
                  </div>
                ))}
              </div>
            )}
            <BracketView tournament={t} onResultSubmit={handleBracketResult} canManage={canCreate} onStartScoreboard={async (roundNum, matchIndex) => {
              try {
                const { data } = await api.post(`/tournaments/${t.id}/bracket-match/${roundNum}/${matchIndex}/start-scoreboard`, { format: "standard" });
                toast.success(data.existing ? "Abriendo marcador" : "Marcador iniciado");
                navigate(`/dashboard/marcador/${data.match_id}`);
              } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
            }} />
          </TabsContent>
        </Tabs>

        {/* Edit Tournament Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md" data-testid="edit-tournament-dialog">
            <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase">Editar Torneo</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Club(es) sede</Label>
                <MultiClubSelect
                  clubs={clubs}
                  value={editForm.club_ids || []}
                  onChange={(ids) => setEditForm((p) => ({ ...p, club_ids: ids, club_id: ids[0] || "" }))}
                  testid="edit-t-club-multi"
                  emptyLabel="Debe tener al menos un club"
                />
              </div>
              <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Nombre</Label>
                <Input data-testid="edit-t-name" value={editForm.name || ""} onChange={(e) => setEditForm(p => ({...p, name: e.target.value}))} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Fecha Inicio</Label>
                  <Input data-testid="edit-t-start" type="date" value={editForm.start_date || ""} onChange={(e) => setEditForm(p => ({...p, start_date: e.target.value}))} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
                <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Fecha Fin</Label>
                  <Input data-testid="edit-t-end" type="date" value={editForm.end_date || ""} onChange={(e) => setEditForm(p => ({...p, end_date: e.target.value}))} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Hora Inicio</Label>
                  <Input data-testid="edit-t-start-time" type="time" value={editForm.start_time || ""} onChange={(e) => setEditForm(p => ({...p, start_time: e.target.value}))} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
                <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Hora Fin</Label>
                  <Input data-testid="edit-t-end-time" type="time" value={editForm.end_time || ""} onChange={(e) => setEditForm(p => ({...p, end_time: e.target.value}))} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
              </div>
              <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Descripcion</Label>
                <Input data-testid="edit-t-desc" value={editForm.description || ""} onChange={(e) => setEditForm(p => ({...p, description: e.target.value}))} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Max Jugadores</Label>
                  <Input data-testid="edit-t-max" type="number" value={editForm.max_players || ""} onChange={(e) => setEditForm(p => ({...p, max_players: parseInt(e.target.value) || 0}))} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
                <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Status</Label>
                  <Select value={editForm.status || ""} onValueChange={(v) => setEditForm(p => ({...p, status: v}))}>
                    <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">
                      <SelectItem value="abierto">Abierto</SelectItem>
                      <SelectItem value="en_curso">En Curso</SelectItem>
                      <SelectItem value="finalizado">Finalizado</SelectItem>
                    </SelectContent>
                  </Select></div>
              </div>
              <DialogFooter><Button onClick={handleEditTournament} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide w-full" data-testid="save-edit-tournament">Guardar</Button></DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tournaments-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Competencias</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Torneos</h1>
        </div>
        {canCreate && (
          <Button onClick={() => { setForm({ name: "", club_id: "", club_ids: [], start_date: "", end_date: "", format: "eliminacion", max_players: 16, description: "", entry_fee: 0, category: "" }); setDialogOpen(true); }}
            className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs" data-testid="create-tournament-btn">
            <Plus size={16} className="mr-2" /> Nuevo Torneo
          </Button>
        )}
        <QuickReportButton section="tournaments" variant="compact" />
      </div>

      {tournaments.length === 0 ? (
        <div className="card-tactical p-12 text-center"><Trophy size={48} weight="duotone" className="text-gray-600 mx-auto mb-4" /><p className="text-gray-400">No hay torneos registrados</p></div>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap" data-testid="tournament-filter-bar">
            <button onClick={() => setFilterCategory("all")} className={`px-3 h-9 text-xs font-semibold uppercase tracking-wider border transition-colors ${filterCategory === "all" ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/20 hover:border-[#CCFF00]/30"}`} data-testid="filter-cat-all">Todas</button>
            {CATEGORIES.map((c) => (
              <button key={c.value} onClick={() => setFilterCategory(c.value)} className={`px-3 h-9 text-xs font-semibold uppercase tracking-wider border transition-colors ${filterCategory === c.value ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/20 hover:border-[#CCFF00]/30"}`} data-testid={`filter-cat-${c.value}`}>
                {c.short}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tournaments.filter((t) => filterCategory === "all" || t.category === filterCategory || (filterCategory === "open" && !t.category)).map((t) => {
            const tClubIds = Array.isArray(t.club_ids) && t.club_ids.length ? t.club_ids : (t.club_id ? [t.club_id] : []);
            const tClubs = tClubIds.map((cid) => clubs.find((c) => c.id === cid)).filter(Boolean);
            const clubLabel = tClubs.length === 0 ? "" : tClubs.length === 1 ? tClubs[0].name : `${tClubs[0].name} +${tClubs.length - 1}`;
            return (
              <div key={t.id} className="card-tactical p-6 cursor-pointer" onClick={() => setSelectedTournament(t)} data-testid={`tournament-card-${t.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white">{t.name}</h3>
                    <p className="text-xs text-gray-500 mt-1" title={tClubs.map((c) => c.name).join(", ")}>{clubLabel}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge variant="outline" className={`badge-status ${statusColors[t.status] || statusColors.abierto}`}>{t.status}</Badge>
                    {t.category && (
                      <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[10px]" data-testid={`tournament-cat-${t.id}`}>
                        {categoryShort(t.category)}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><CalendarBlank size={14} />{t.start_date}</span>
                  <span className="flex items-center gap-1"><Users size={14} />{t.players?.length || 0}/{t.max_players}</span>
                  {t.brackets?.length > 0 && <span className="flex items-center gap-1 text-[#CCFF00]"><Tree size={14} />Brackets</span>}
                </div>
              </div>
            );
          })}
          </div>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-lg" data-testid="tournament-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase">Nuevo Torneo</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Nombre</Label>
              <Input data-testid="tournament-name-input" value={form.name} onChange={(e) => update("name", e.target.value)}
                className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" required />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Club(es) sede</Label>
              <MultiClubSelect
                clubs={clubs}
                value={form.club_ids || []}
                onChange={(ids) => setForm((p) => ({ ...p, club_ids: ids, club_id: ids[0] || "" }))}
                testid="tournament-club-multi"
                emptyLabel="Selecciona al menos un club"
              />
              <p className="text-[10px] text-gray-500 mt-1">El torneo podrá disputarse en las canchas de todos los clubes seleccionados.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Fecha Inicio</Label>
                <Input data-testid="tournament-start-input" type="date" value={form.start_date} onChange={(e) => update("start_date", e.target.value)}
                  className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" required />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Fecha Fin</Label>
                <Input data-testid="tournament-end-input" type="date" value={form.end_date} onChange={(e) => update("end_date", e.target.value)}
                  className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Hora Inicio</Label>
                <Input data-testid="tournament-start-time" type="time" value={form.start_time} onChange={(e) => update("start_time", e.target.value)}
                  className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Hora Fin</Label>
                <Input data-testid="tournament-end-time" type="time" value={form.end_time} onChange={(e) => update("end_time", e.target.value)}
                  className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Formato</Label>
                <Select value={form.format} onValueChange={(v) => update("format", v)}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="tournament-format-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="eliminacion">Eliminacion</SelectItem>
                    <SelectItem value="round_robin">Round Robin</SelectItem>
                    <SelectItem value="grupos">Grupos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Max Jugadores</Label>
                <Input data-testid="tournament-maxplayers-input" type="number" min="2" value={form.max_players} onChange={(e) => update("max_players", e.target.value)}
                  className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" />
              </div>
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Categoria (opcional)</Label>
              <Select value={form.category || "__all"} onValueChange={(v) => update("category", v === "__all" ? "" : v)}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="tournament-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="__all">Abierto a todas las categorias</SelectItem>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label} - {c.description}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-gray-500 mt-1.5">Si seleccionas una categoria, solo jugadores de esa categoria podran inscribirse.</p>
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Liga (opcional)</Label>
              <Select value={form.league_id || "__none"} onValueChange={(v) => update("league_id", v === "__none" ? "" : v)}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="tournament-league-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="__none">Sin liga</SelectItem>
                  {leagues.map((lg) => (
                    <SelectItem key={lg.id} value={lg.id}>{lg.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 p-3 bg-black/40 border border-white/10 rounded">
              <input
                id="tournament-require-approval"
                type="checkbox"
                checked={!!form.require_approval}
                onChange={(e) => update("require_approval", e.target.checked)}
                className="w-4 h-4 accent-[#CCFF00]"
                data-testid="tournament-require-approval"
              />
              <label htmlFor="tournament-require-approval" className="text-xs text-gray-300 cursor-pointer">
                <b className="text-[#CCFF00] uppercase tracking-widest">Requerir aprobación</b> · Las solicitudes de jugadores quedarán pendientes hasta que un admin o directivo del club las autorice.
              </label>
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide w-full" data-testid="tournament-submit-btn">Crear Torneo</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {pendingFor && (
        <PendingApprovalsDialog
          open={!!pendingFor}
          onClose={() => setPendingFor(null)}
          kind="tournament"
          eventId={pendingFor.id}
          eventName={pendingFor.name}
          onChanged={loadTournaments}
        />
      )}
    </div>
  );
}

function PlayerBadge({ playerId, index }) {
  const [name, setName] = useState("...");
  useEffect(() => { api.get(`/users/${playerId}`).then(({ data }) => setName(data.name)).catch(() => {}); }, [playerId]);
  return <span className="bg-[#0A0A0A] border border-white/10 text-xs text-gray-300 px-3 py-1 font-semibold">{String(index + 1).padStart(2, "0")} {name}</span>;
}
